import {isColorBlindMode} from "./colorblind.js";

const VIRIDIS_STOPS = [
    [68, 1, 84],
    [59, 82, 139],
    [33, 145, 140],
    [94, 201, 98],
    [253, 231, 37],
];

function clamp01(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(1, Math.max(0, value));
}

function lerpViridisTuple(t) {
    const pct = clamp01(t);
    if (pct <= 0) return VIRIDIS_STOPS[0].slice();
    if (pct >= 1) return VIRIDIS_STOPS[VIRIDIS_STOPS.length - 1].slice();
    const idx = pct * (VIRIDIS_STOPS.length - 1);
    const i = Math.floor(idx);
    const frac = idx - i;
    const c0 = VIRIDIS_STOPS[i];
    const c1 = VIRIDIS_STOPS[i + 1];
    const r = Math.round(c0[0] + frac * (c1[0] - c0[0]));
    const g = Math.round(c0[1] + frac * (c1[1] - c0[1]));
    const b = Math.round(c0[2] + frac * (c1[2] - c0[2]));
    return [r, g, b];
}

function lerpViridis(t) {
    const [r, g, b] = lerpViridisTuple(t);
    return `rgb(${r}, ${g}, ${b})`;
}

export function scaleNumber(n, fixed=2) {
    if (n >= 1000000000000) {
        return (n / 1000000000000).toFixed(fixed) + "T";
    } else if (n >= 1000000000) {
        return (n / 1000000000).toFixed(fixed) + "G";
    } else if (n >= 1000000) {
        return (n / 1000000).toFixed(fixed) + "M";
    } else if (n >= 1000) {
        return (n / 1000).toFixed(fixed) + "K";
    }
    // For small numbers, format them properly
    if (typeof n === 'number') {
        return n.toFixed(fixed);
    }
    return n;
}

function trimScaledDigits(value) {
    return value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
}

export function formatMbpsCapacity(mbps, fixed = 2) {
    const n = Number(mbps);
    if (!Number.isFinite(n)) {
        return "-";
    }
    if (n <= 0) {
        return "0 Mbps";
    }

    const scaled = scaleNumber(n * 1_000_000, fixed);
    const match = /^([0-9.]+)([KMGT]?)$/.exec(scaled);
    if (!match) {
        return `${trimScaledDigits(String(n.toFixed(fixed)))} Mbps`;
    }

    const [, value, suffix] = match;
    const unit = {
        "": "bps",
        K: "Kbps",
        M: "Mbps",
        G: "Gbps",
        T: "Tbps",
    }[suffix] || "bps";

    return `${trimScaledDigits(value)} ${unit}`;
}

export function scaleNanos(n, precision=2) {
    if (n === 0) return "-";
    if (n > 60000000000) {
        return (n / 60000000000).toFixed(precision) + "m";
    }else if (n > 1000000000) {
        return (n / 1000000000).toFixed(precision) + "s";
    } else if (n > 1000000) {
        return (n / 1000000).toFixed(precision) + "ms";
    } else if (n > 1000) {
        return (n / 1000).toFixed(precision) + "µs";
    }
    return n + "ns";
}

export function colorRamp(n) {
    if (isColorBlindMode()) {
        if (n <= 100) {
            return lerpViridis(0);
        } else if (n <= 150) {
            return lerpViridis(0.5);
        } else {
            return lerpViridis(1);
        }
    }
    if (n <= 100) {
        return "#aaffaa";
    } else if (n <= 150) {
        return "goldenrod";
    } else {
        return "#ffaaaa";
    }
}

export function rttCircleSpan(rtt) {
    let span = document.createElement("span");
    span.style.color = colorRamp(rtt);
    span.innerText = "⬤";
    return span;
}

export function lerpGreenToRedViaOrange(value, max) {
    if (isColorBlindMode()) {
        const t = max > 0 ? value / max : 0;
        return lerpViridis(t);
    }
    let r = 0;
    let g = 0;
    let b = 0;
    if (value < max / 2) {
        r = 255;
        g = Math.floor(255 * value / (max / 2));
    } else {
        r = Math.floor(255 * (max - value) / (max / 2));
        g = 255;
    }
    return `rgb(${r}, ${g}, ${b})`;
}

export function lerpGreenToRedViaOrangeTuple(value, max) {
    if (isColorBlindMode()) {
        const t = max > 0 ? value / max : 0;
        return lerpViridisTuple(t);
    }
    let r = 0;
    let g = 0;
    let b = 0;
    if (value < max / 2) {
        r = 255;
        g = Math.floor(255 * value / (max / 2));
    } else {
        r = Math.floor(255 * (max - value) / (max / 2));
        g = 255;
    }
    return [r, g, b];
}

export function formatThroughput(throughput, limitInMbps) {
    let limitBits = limitInMbps * 1000 * 1000;
    let percent = 0;
    if (limitBits > 0) {
        percent = (throughput / limitBits) * 100;
    }
    let color = lerpGreenToRedViaOrange(100-percent, 100);
    let blob = "<span class='muted' style='color: " + color + "'>■</span>";
    blob += "<span>" + scaleNumber(throughput, 1) + "bps</span>";
    return blob;
}

export function formatRtt(rtt) {
    if (rtt === undefined || rtt === null || rtt.nanoseconds === 0) {
        return "-";
    }
    const limit = 200;
    let percent = 0;
    if (limit > 0) {
        percent = (rtt / limit) * 100;
    }
    let color = lerpGreenToRedViaOrange(100-percent, 100);
    let blob = "<span class='muted' style='color: " + color + "'>■</span>";
    blob += "<span>" + parseFloat(rtt).toFixed(0) + "ms</span>";
    return blob;
}

export function formatRetransmit(retransmits, digits = 1) {
    if (!Number.isFinite(retransmits)) {
        return "-";
    }
    const value = Math.max(0, retransmits);
    let percent = Math.min(100, value) / 100;
    let color = isColorBlindMode() ? lerpViridis(percent) : lerpColor([0, 255, 0], [255, 0, 0], percent);
    return "<span class='muted' style='color: " + color + "'>■</span>" + value.toFixed(digits) + "%</span>";
}

export function formatRetransmitPercent(retransmits) {
    let percent = Math.min(100, retransmits) / 100;
    let color = isColorBlindMode() ? lerpViridis(percent) : lerpColor([0, 255, 0], [255, 0, 0], percent);
    return "<span class='muted' style='color: " + color + "'>■</span>" + retransmits.toFixed(1) + "</span>";
}

export function formatCakeStat(n) {
    let percent = Math.min(100, n) / 100;
    let color = lerpColor([128, 128, 0], [255, 255, 255], percent);
    let html = "<span class='muted' class='retransmits' style='color: " + color + "'>";
    html += n;
    html += "</span>";
    return html;
}

export function lerpColor(color1, color2, weight) {
    var r = Math.round(color1[0] + (color2[0] - color1[0]) * weight);
    var g = Math.round(color1[1] + (color2[1] - color1[1]) * weight);
    var b = Math.round(color1[2] + (color2[2] - color1[2]) * weight);
    return `rgb(${r}, ${g}, ${b})`;
}

export function formatPercent(percent, digits=0) {
    let color = lerpGreenToRedViaOrange(100-Math.min(100,percent), 100);
    return "<span class='muted' style='color: " + color + "'>" + percent.toFixed(digits) + "%</span>";
}

export function rttNanosAsSpan(rttNanos, precision=0) {
    let rttInMs = Math.min(200, rttNanos / 1000000);
    let color = lerpGreenToRedViaOrange(200 - rttInMs, 200);
    let html = "<span class='muted' style='color: " + color + "'>■</span> " + scaleNanos(rttNanos, precision);
    return html;
}
